import { useCallback, useEffect, useRef, useMemo, useState } from 'react'
import { useAppSelector } from '@/store'
import useAuth from '@/features/base/user-management/auth/hooks/useAuth'
import { logger } from '@/utils/logger'

function useIdleLogout(defaultTimeout: number = 10 * 60 * 1000) {
    const { signOut } = useAuth()
    const token = useAppSelector((state) => state.auth.session.token)
    const expTime = useAppSelector((state) => state.auth.user.expTime)
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const [isInitialized, setIsInitialized] = useState(false)

    // Calculate the initial timeout based on expTime from Redux store
    const derivedTimeout = useMemo(() => {
        if (!expTime) return defaultTimeout
        const remaining = expTime - Date.now()
        return Math.max(0, Math.min(remaining, defaultTimeout))
    }, [expTime, defaultTimeout])

    // Create a stable reset function
    const resetTimer = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current)
            timerRef.current = null
        }

        if (token) {
            timerRef.current = setTimeout(async () => {
                logger.warn('Session expired. Please sign in again.')
                try {
                    await signOut()
                } catch (e) {
                    /* ignore */
                }
            }, derivedTimeout)
        }
    }, [token, derivedTimeout, signOut])

    // Set up event listeners and initial timer
    useEffect(() => {
        if (!token) return // Don't set up timers if there's no token

        // Only initialize the timer once
        if (!isInitialized) {
            resetTimer()
            setIsInitialized(true)
        }

        const events = [
            'mousemove',
            'mousedown',
            'keypress',
            'keydown',
            'scroll',
            'touchstart',
            'click',
            'input',
            'wheel',
        ] as const

        // Throttle the activity handler to prevent excessive calls
        let throttleTimer: NodeJS.Timeout | null = null
        const handleActivity = () => {
            if (!throttleTimer) {
                throttleTimer = setTimeout(() => {
                    resetTimer()
                    throttleTimer = null
                }, 1000) // Throttle to once per second
            }
        }

        // Add event listeners
        events.forEach((event) => {
            globalThis.addEventListener(event, handleActivity, {
                passive: true,
            })
        })

        // Cleanup function
        return () => {
            events.forEach((event) => {
                globalThis.removeEventListener(event, handleActivity)
            })
            if (timerRef.current) {
                clearTimeout(timerRef.current)
                timerRef.current = null
            }
            if (throttleTimer) {
                clearTimeout(throttleTimer)
            }
        }
    }, [token, resetTimer, isInitialized])
}

export default useIdleLogout
